import { useEffect, useRef, useState } from 'react';
import { pendoInitializer } from '../lib/util/pendo';
import { facebookTPInitializer } from '../lib/util/facebookTP';
import { isUserInChina } from '../lib/util/isUserInChina';
import { gtmInitializer } from '../lib/util/gtm';
import { UserProps } from '@elsevier-health/osmosis-shared/types/user';
import { useUserContext } from '../context/UserContext';

// Extract the initialization logic into a separate, testable function
/**
 * Initializes third-party scripts based on user and location.
 * @param {UserProps | null} user - The user object or null.
 * @param {boolean} isInChina - Whether the user is located in China.
 */
export const initializeThirdPartyScripts = (
  user: UserProps | null,
  isInChina: boolean,
) => {
  pendoInitializer(user);
  gtmInitializer(user);
  if (!isInChina) {
    facebookTPInitializer();
  }
};

/**
 * Checks if the user is located in China.
 * @returns {Promise<boolean>} A promise that resolves to true if the user is in China, otherwise false.
 */
export const checkUserLocation = async (): Promise<boolean> => {
  try {
    return await isUserInChina();
  } catch (error) {
    return false;
  }
};

/**
 * Initializes third party scripts.
 */
export default function usePageInitializers(
  options: {
    user?: UserProps;
  } = {},
) {
  const { user: serverUser } = options;
  const { user: clientUser, isUserLoaded } = useUserContext();
  const [isInChina, setIsInChina] = useState<boolean | null>(null);
  const [isLocationChecked, setIsLocationChecked] = useState(false);
  const locationCheckRef = useRef<boolean | null>(null);

  // Check location once
  useEffect(() => {
    if (locationCheckRef.current) return;
    const performLocationCheck = async () => {
      locationCheckRef.current = true;
      const result = await checkUserLocation();
      setIsInChina(result);
      setIsLocationChecked(true);
    };

    performLocationCheck();
  }, []); // Run only once

  // Initialize scripts when both user and location are ready
  useEffect(() => {
    if (!isUserLoaded || !isLocationChecked || isInChina === null) return;
    const user = serverUser || clientUser;
    initializeThirdPartyScripts(user, isInChina);
  }, [clientUser, serverUser, isUserLoaded, isLocationChecked, isInChina]);

  return {
    isInChina,
    isLocationChecked,
    isUserLoaded,
  };
}
